import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { LOGO_WHITE, LOGO_DARK } from "@/lib/brand";
import { buildWhatsAppLink } from "@/lib/api";

/**
 * Shared site header used on PublicSite + Blog pages.
 *
 * Props:
 *  - settings: { whatsapp_number }
 *  - onBook: optional callback to open the enquiry dialog (mobile drawer "Send an enquiry")
 *  - alwaysSolid: when true, the nav uses the "scrolled" (dark text on white) style at the
 *    top of the page too. Use this on pages without a dark hero behind the nav.
 *  - sectionPrefix: prepended to hash links (e.g. "/" on blog pages so #about navigates home).
 */
export default function PublicNav({ settings, onBook, alwaysSolid = false, sectionPrefix = "" }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 30);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const wa = buildWhatsAppLink(settings?.whatsapp_number);
  const solid = scrolled || alwaysSolid;

  const links = [
    { label: "About", href: `${sectionPrefix}#about` },
    { label: "Location", href: `${sectionPrefix}#location` },
    { label: "Amenities", href: `${sectionPrefix}#amenities` },
    { label: "Gallery", href: `${sectionPrefix}#gallery` },
    { label: "Pricing", href: `${sectionPrefix}#pricing` },
    { label: "Blog", href: "/blog", internal: true },
  ];

  const LinkOrAnchor = ({ link, className, onClick, children }) => {
    if (link.internal) {
      return (
        <Link to={link.href} className={className} onClick={onClick}>
          {children}
        </Link>
      );
    }
    return (
      <a href={link.href} className={className} onClick={onClick}>
        {children}
      </a>
    );
  };

  return (
    <header
      data-testid="public-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        solid ? "bg-white/85 backdrop-blur-xl border-b border-earth-100/60 py-3" : "bg-transparent py-4 sm:py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-logo">
          <img
            src={solid ? LOGO_DARK : LOGO_WHITE}
            alt="Aiyavi Retreat"
            className="h-10 sm:h-14 w-auto object-contain transition-opacity duration-300"
            draggable={false}
          />
        </Link>
        <nav className="hidden md:flex items-center gap-9 text-base">
          {links.map((l) => (
            <LinkOrAnchor
              key={l.label}
              link={l}
              className={`nav-link transition-colors font-medium ${solid ? "text-earth-700 hover:text-[#FF6B35]" : "text-white/90 hover:text-[#FF6B35]"}`}
            >
              {l.label}
            </LinkOrAnchor>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp-btn"
            className={`hidden sm:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300 ${
              solid
                ? "bg-earth-900 text-white hover:bg-[#FF6B35]"
                : "bg-white/15 backdrop-blur-md border border-white/40 text-white hover:bg-[#FF6B35] hover:border-[#FF6B35]"
            }`}
          >
            Book Now <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </a>
          <button
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-mobile-btn"
            className={`md:hidden w-11 h-11 rounded-full flex items-center justify-center transition-colors ${
              solid ? "bg-earth-900 text-white" : "bg-white/15 backdrop-blur-md border border-white/40 text-white"
            }`}
          >
            <Menu className="w-5 h-5" strokeWidth={1.7} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`md:hidden fixed inset-0 z-[70] transition-all duration-500 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="mobile-drawer"
      >
        <div
          className="absolute inset-0 bg-earth-900/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
        <div
          className={`absolute top-0 right-0 h-full w-[85%] max-w-sm bg-earth-50 flex flex-col shadow-[-30px_0_80px_-30px_rgba(0,0,0,0.3)] transition-transform duration-500 ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between px-6 py-5 border-b border-earth-100">
            <img src={LOGO_DARK} alt="Aiyavi Retreat" className="h-10 w-auto object-contain" />
            <button
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              data-testid="mobile-close-btn"
              className="w-10 h-10 rounded-full flex items-center justify-center bg-white border border-earth-200"
            >
              <X className="w-5 h-5 text-earth-900" strokeWidth={1.5} />
            </button>
          </div>
          <nav className="flex flex-col px-6 py-6 gap-1">
            {links.map((l, i) => (
              <LinkOrAnchor
                key={l.label}
                link={l}
                onClick={() => setOpen(false)}
                className="group flex items-center justify-between py-4 border-b border-earth-100 text-earth-900 text-2xl font-serif"
              >
                <span>{l.label}</span>
                <ArrowUpRight
                  className="w-4 h-4 text-earth-400 group-hover:text-[#FF6B35] transition-colors"
                  strokeWidth={1.5}
                  style={{ transitionDelay: `${i * 40}ms` }}
                />
              </LinkOrAnchor>
            ))}
          </nav>
          <div className="mt-auto p-6 flex flex-col gap-3">
            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-2 bg-[#FF6B35] text-white rounded-full px-5 py-4 font-medium"
            >
              Book Now <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
            {onBook && (
              <button
                onClick={() => {
                  setOpen(false);
                  onBook();
                }}
                className="inline-flex items-center justify-center gap-2 bg-white border border-earth-200 text-earth-900 rounded-full px-5 py-4 font-medium"
              >
                Send an enquiry
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
